import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import CauzioniInfoModal from './CauzioniInfoModal';
import { getCauzioniCatalogue, getCauzioniSubProductDocuments } from '../../services/axios-client/axeCauzioni';

function CauzioniRisks() {
  const { subProductSlug } = useParams();
  const navigate = useNavigate();
  const [subProduct, setSubProduct] = useState(null);
  const [documentsByRisk, setDocumentsByRisk] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [infoRiskId, setInfoRiskId] = useState(null);

  useEffect(() => {
    let cancelled = false;

    getCauzioniCatalogue()
      .then((res) => {
        const found = res.sub_products.find((sub) => sub.slug === subProductSlug);
        if (cancelled) return null;
        setSubProduct(found ?? null);
        if (!found) return null;

        // One call covers every rischio, so the counts below cost nothing extra.
        return getCauzioniSubProductDocuments(found.id).then((docRes) => {
          if (cancelled) return;
          const map = {};
          docRes.risks.forEach((r) => { map[r.id] = r; });
          setDocumentsByRisk(map);
        });
      })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [subProductSlug]);

  if (loading) {
    return <div className="cauzioni-page"><p className="cauzioni-count">Caricamento...</p></div>;
  }

  if (error) {
    return (
      <div className="cauzioni-page">
        <p className="cauzioni-count cauzioni-error">
          Impossibile caricare la garanzia. Riprova più tardi.
        </p>
      </div>
    );
  }

  if (!subProduct) {
    return (
      <div className="cauzioni-page">
        {/* Rischio selection is still part of choosing, not the quote flow — a broker who picked
          the wrong garanzia needs a way back without restarting from Nuovo preventivo. */}
        <button
          type="button"
          className="cauzioni-back"
          onClick={() => navigate('/quote/surety-bonds')}
        >
          ← Tutte le garanzie
        </button>
        <p className="cauzioni-count">Garanzia non trovata.</p>
      </div>
    );
  }

  return (
    <div className="cauzioni-page" data-testid="cauzioni-risks">
      {/* Choosing a rischio is still part of choosing, not the quote flow — a broker who opened
          the wrong garanzia needs a way back without restarting from Nuovo preventivo. */}
      <button
        type="button"
        className="cauzioni-back"
        onClick={() => navigate('/quote/surety-bonds')}
        data-testid="cauzioni-back"
      >
        ← Tutte le garanzie
      </button>

      <div className="cauzioni-head">
        <h1>{subProduct.name.toUpperCase()}</h1>
        {subProduct.description && (
          <p className="cauzioni-head-reference">{subProduct.description}</p>
        )}
        <p>Scegli il rischio da garantire</p>
      </div>

      <p className="cauzioni-count">
        {subProduct.risks.length === 1
          ? '1 rischio disponibile'
          : `${subProduct.risks.length} rischi disponibili`}
      </p>

      <div className="cauzioni-grid">
        {subProduct.risks.map((risk) => {
          const docs = documentsByRisk[risk.id];
          const required = docs?.required.length ?? 0;
          const onRequest = docs?.on_request.length ?? 0;
          const total = required + onRequest;

          return (
            <div className="cauzioni-card" key={risk.id} data-testid="cauzioni-risk-option">
              <div className="cauzioni-card-body">
                <h2 className="cauzioni-card-title">{risk.name}</h2>
                {docs && (
                  <span className="cauzioni-badge">
                    {total === 0 ? 'Nessun documento' : `${total} document${total === 1 ? 'o' : 'i'}`}
                  </span>
                )}
              </div>
              <div className="cauzioni-card-actions">
                <button
                  type="button"
                  className="btn btn-outline-success btn-sm"
                  onClick={() => setInfoRiskId(risk.id)}
                  data-testid="cauzioni-info"
                >
                  Info
                </button>
                <button
                  type="button"
                  className="btn btn-success btn-sm"
                  onClick={() => navigate(
                    `/quote/surety-bonds/${subProduct.slug}/request?risk=${risk.id}`,
                  )}
                  data-testid="cauzioni-request"
                >
                  Richiedi
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {infoRiskId && (
        <CauzioniInfoModal
          subProduct={subProduct}
          riskId={infoRiskId}
          onClose={() => setInfoRiskId(null)}
        />
      )}
    </div>
  );
}

export default CauzioniRisks;
